import {AfterViewInit, Component, Input, OnInit} from '@angular/core';
import {FormBuilder, FormGroup} from '@angular/forms';
import {TranslateService} from '@ngx-translate/core';
import {combineLatest, Observable, of, Subject} from 'rxjs';
import {finalize, map, switchMap, tap} from 'rxjs/operators';
import {IJob} from 'src/app/batches/jobs';
import {MICalendarEvent} from 'src/app/calendar/calendar';
import {CalendarService} from 'src/app/calendar/calendar.service';
import {PermissionService} from 'src/app/core/permission.service';
import {IUser} from 'src/app/users/users';
import {HelpersService} from '../../helpers.service';
import {BatchesWidgetService} from './batches-widget.service';

export interface IFormFilters {
  description: string;
  activityId: number;
  store: string;
}
@Component({
    selector: 'app-unscheduled-batches-widget',
    templateUrl: './unscheduled-batches-widget.component.html',
    styleUrls: ['./unscheduled-batches-widget.component.scss'],
    standalone: false
})
export class UnscheduledBatchesWidgetComponent implements OnInit, AfterViewInit {

  isLoading = false;
  widgetFormGroup: FormGroup;
  @Input() public set startDateFrom(date: string) {
    if (date !== null) {
      this.startDateFromSubject.next(date.substring(0,10));
    }
  }
  @Input() public set startDateTo(date: any) {
    if (date !== null) {
      this.startDateToSubject.next(date.substring(0,10));
    }
  }

  _user: IUser;
  @Input() public set user(data: IUser) {
    this._user = data;
    this.checkDownlineUserChanged();
  }
  filtersForm: FormGroup;
  batches$: Observable<MICalendarEvent[]> = of([]);
  rawBatches$: Observable<IJob[]>;
  private startDateFromSubject = new Subject<string>();
  startDateFrom$ = this.startDateFromSubject.asObservable();
  private startDateToSubject = new Subject<string>();
  startDateTo$ = this.startDateToSubject.asObservable();
  private downlineUserIdSubject = new Subject<number>();
  downlineUserId$ = this.downlineUserIdSubject.asObservable();
  hasData = true;

  batchesArray: IJob[] = [];
  constructor(
    private fb: FormBuilder,
    public service: BatchesWidgetService,
    public calendarService : CalendarService,
    public translateService: TranslateService,
    public permissionService: PermissionService,
    public helpersService: HelpersService,
  ) {
  }

  ngOnInit() {
    this.filtersForm = this.fb.group({
      activityId: [''],
      description: [''],
      store: ['']
    });


    this.permissionService.hasPermission('ACTIVITY').subscribe(hasPermission => {
      if (hasPermission) {
        this.service.callForDataSubject.next(true);
        this.rawBatches$ = combineLatest([
          this.startDateFrom$,
          this.startDateTo$,
          this.downlineUserId$,
          this.service.callForData$,
        ]).pipe(
          tap((value) => this.isLoading = true),
          switchMap(filters => this.service.getUnScheduledJobs(filters[0], filters[1], filters[2])
            .pipe(
              finalize(() => this.isLoading = false),
              map(response => response['list']),
            )
          )
        );

        this.batches$ = combineLatest([
          this.rawBatches$,
          this.filtersForm.valueChanges
        ]).pipe(
          switchMap( ([items, filters]) => {
              this.hasData = items.length > 0;

              return of(items
                .filter(item => this.matchesFilters(item, filters))
                .filter(item => item.scheduledStartDateTime === null)
                .map(item => this.calendarService.transformJobEventToMICalendarEvent(item))
              )
            }
          ),
        )

      }
    });


    this.service.resetFilters$.subscribe(() => this.resetFilters());
  }

  ngAfterViewInit() {
    this.checkDownlineUserChanged();
  }

  public displayName(event:MICalendarEvent): string {
    const item = event.jobDetails;
    return [item?.id, item?.description, item?.storeName].join(' - ');
  }

  public getUnscheduledJobEvent(event) {
    return JSON.stringify(event);
  }

  private resetFilters() {
    this.filtersForm.patchValue({activityId: undefined, description: undefined, store: undefined});
  }

  private matchesFilters(item: IJob, filters: IFormFilters): boolean  {
    return (!filters.activityId || this.matches(item.id, filters.activityId) ) &&
           (!filters.description || this.matches(item.description, filters.description)) &&
           (!filters.store || (this.matches(item.storeName, filters.store) || this.matches(item.storeNickname, filters.store) || this.matches(item.storeNumber, filters.store)));
  }

  private matches(value, filter) {
    return `${value}`.toLowerCase().includes(`${filter}`.toLowerCase());
  }

  private checkDownlineUserChanged() {
    if (this._user && this._user.isDownlineUser) {
      this.downlineUserIdSubject.next(this._user.userId);
    } else {
      this.downlineUserIdSubject.next(null);
    }
  }
}
